import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import PublicLayout from "../../../layouts/PublicLayout";
import styles from "./NewsletterPage.module.css";
import { Helmet } from "react-helmet";

type FormValues = { email: string };

export default function NewsletterPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>();

  const handleSubscribe = async (data: FormValues) => {
    // Show "Subscribed!" immediately for static flow showcase
    setIsSuccess(true);
    reset();

    // Fire API in background silently — integrate success/error logic later
    const KLAVIYO_SITE_ID = process.env.REACT_APP_KLAVIYO_SITE_ID || "YOUR_SITE_ID";
    const KLAVIYO_LIST_ID = process.env.REACT_APP_KLAVIYO_LIST_ID || "YOUR_LIST_ID";

    fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_SITE_ID}`, {
      method: "POST",
      headers: {
        "revision": "2024-02-15",
        "content-type": "application/json"
      },
      body: JSON.stringify({
        data: {
          type: "subscription",
          attributes: {
            custom_source: "Newsletter Page",
            profile: {
              data: {
                type: "profile",
                attributes: { email: data.email }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: "list",
                id: KLAVIYO_LIST_ID
              }
            }
          }
        }
      })
    }).catch(() => { /* silently ignored — error handling TODO */ });
  };

  return (
    <PublicLayout>
      <Helmet>
        <title>Join the Newsletter | Sugar Cribs</title>
        <meta name="description" content="Stay up to date with the latest SugarCribs news, creator spotlights, and early access opportunities." />
      </Helmet>

      <div className={styles.pageContainer}>
        {/* ── Hero Section ── */}
        <section className={styles.heroSection}>
          <div className={styles.heroInner}>
            <div className={styles.heroContent}>
              <h1 className={styles.heroTitle}>Newsletter Signup</h1>
              <p className={styles.heroSubtitle}>
                Step behind the velvet rope and see what our founding creators are building.
              </p>
              <div className={styles.breadcrumbs}>
                <Link to="/" style={{ color: "inherit", textDecoration: "none", marginRight: '7px' }}>Home</Link>
                <span>
                  <svg width="6" height="11" viewBox="0 0 6 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.500001 10.5L5.5 5.5L0.5 0.500001" stroke="white" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </span>
                <span className={styles.breadcrumbActive}>Join the Newsletter</span>
              </div>
            </div>
            <div className={styles.heroImageContainer}>
              {/* Hero Image */}
              <img
                src="/images/news_letter.png"
                alt="Newsletter Hero"
                className={styles.heroImage}
              />
            </div>
          </div>
        </section>

        {/* ── Subscription Section ── */}
        <section className={styles.subscribeSection}>
          <div className={styles.subscribeInner}>
            <h2 className={styles.sectionTitle}>Stay Connected with SugarCribs</h2>
            <p className={styles.sectionSubtitle}>
              Stay up to date with the latest SugarCribs news, creator spotlights, exclusive announcements, and early access opportunities.
            </p>

            <div className={styles.avatarStack}>
              <div className={styles.avatars}>
                <img src="/images/people1.png" alt="User" className={styles.avatar} />
                <img src="/images/people2.png" alt="User" className={styles.avatar} />
                <img src="/images/people3.png" alt="User" className={styles.avatar} />
                <img src="/images/people4.png" alt="User" className={styles.avatar} />
                <div className={`${styles.avatar} ${styles.avatarCount}`}>
                  <span>10K+</span>
                </div>
              </div>
              <span className={styles.avatarText}>People already joined</span>
            </div>

            <form className={styles.formGroup} onSubmit={handleSubmit(handleSubscribe)}>
              <input
                placeholder="Enter your Email"
                className={styles.inputField}
                disabled={isLoading || isSuccess}
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Please enter a valid email address",
                  },
                })}
              />
              <button type="submit" className={`${styles.metallicBtn} ${styles.subscribeBtn}`} disabled={isLoading || isSuccess}>
                {isLoading ? "Subscribing..." : isSuccess ? "Subscribed!" : "Subscribe Now"}
              </button>
            </form>
            {errors.email && (
              <p style={{ color: "#FE5A97", marginTop: "8px", fontSize: "13px" }}>
                {errors.email.message}
              </p>
            )}
            {isSuccess && <p style={{ color: "#4CAF50", marginTop: "12px", fontSize: "14px", fontWeight: "bold" }}>Thank you for subscribing!</p>}

            <p className={styles.privacyText}>
              By subscribing, you agree to receive emails from SugarCribs. Unsubscribe anytime. See our <Link to="/privacy-policy" className={styles.privacyLink}>Privacy Policy</Link>
            </p>
          </div>
        </section>

        {/* ── Community Card Section ── */}
        <section className={styles.communitySection}>
          <div className={styles.communityCard}>
            <div className={styles.cardOverlay} />
            <h2 className={styles.communityTitle}>Become Part of the SugarCribs<br />Community</h2>
            <p className={styles.communitySubtitle}>
              Whether you're looking to discover exclusive creators or become one yourself, your journey starts here.
            </p>
            <div className={styles.cardButtons}>
              <Link to="/register">
                <button className={`${styles.metallicBtn}  ${styles.subscribeBtn}`}>Become A Member</button>
              </Link>
              <Link to="/creators">
                <button className={styles.outlineBtn}>Apply As Creator</button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}
